import React, { useEffect } from "react";

import {
  EmployeesApi,
  LoginApi,
  Configuration as LoginApiConfiguration,
} from "@its4plan/authentication";

import { getLoginToken, saveLoginToken } from "../utils/local-storage.helpers";
import { CompanyApi } from "./clients/company.client";

export type ApiContext = {
  loginApi: LoginApi;
  companyApi: CompanyApi;
  employeeApi: EmployeesApi;
};

const ApiContext = React.createContext<ApiContext | null>(null);

export function useApiContext() {
  const context = React.useContext(ApiContext);
  if (!context) {
    throw new Error("useApiContext must be used within an ApiProvider");
  }
  return context;
}

export function useLoginApi() {
  return useApiContext().loginApi;
}

export function useCompanyApi() {
  return useApiContext().companyApi;
}

export function useEmployeeApi() {
  return useApiContext().employeeApi;
}

export function ApiContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { authToken } = useLoginContext();
  return (
    <ApiContext.Provider value={registerApis(authToken)}>
      {children}
    </ApiContext.Provider>
  );
}

function configFactory(authToken: string | undefined, baseUrl: string) {
  return {
    basePath: baseUrl,
    headers: { Authorization: `Bearer ${authToken}` },
  };
}

export function baseUrlFactory(prefix: string, fallback: string) {
  const hostname = window.location.hostname;
  if (hostname === "localhost") {
    return fallback;
  }
  const [_, ...rest] = hostname.split(".");
  return "https://" + prefix + "." + rest.join(".");
}

function createConfigurations(authToken: string | undefined) {
  return {
    its4ServerConfig: {
      baseUrl: baseUrlFactory(
        "api",
        import.meta.env.VITE_APP_PROD_URL as string,
      ),
    },
    loginConfig: new LoginApiConfiguration(
      configFactory(
        authToken,
        baseUrlFactory(
          "auth",
          import.meta.env.VITE_APP_AUTHENTICATION_URL as string,
        ),
      ),
    ),
  };
}

function registerApis(authToken: string | undefined) {
  const { loginConfig, its4ServerConfig } = createConfigurations(authToken);
  return {
    loginApi: new LoginApi(loginConfig),
    companyApi: new CompanyApi(its4ServerConfig),
    employeeApi: new EmployeesApi(loginConfig),
  };
}

const LoginContext = React.createContext<{
  authToken: string | undefined;
  setAuthToken: (token: string | undefined) => void;
} | null>(null);

export function useLoginContext() {
  const context = React.useContext(LoginContext);
  if (!context) {
    throw new Error("useLoginContext must be used within a LoginProvider");
  }
  return context;
}

export function LoginContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [authToken, setAuthToken] = React.useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    const loginToken = getLoginToken();
    if (loginToken) {
      setAuthToken(loginToken);
    }
  }, []);

  return (
    <LoginContext.Provider
      value={{
        authToken,
        setAuthToken: (token) => {
          saveLoginToken(token);
          setAuthToken(token);
        },
      }}
    >
      {children}
    </LoginContext.Provider>
  );
}
